import { Autocomplete, Alert, Button, Chip, Grid, Paper, SnackbarContent, TextField, Typography, Snackbar, Dialog, DialogActions, DialogContent, CircularProgress } from "@mui/material";
import { Box, Stack } from "@mui/system";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useContext, useEffect, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useLocation, useNavigate } from "react-router-dom";
import { DesignTemplatesEndPointLogic } from "../../../../../logic/AssetManagement/Design/Templates/DesignTemplatesEndPointLogic";
import { selectUserToken, setIsLoading, } from "../../../../../features/userSlice";
import SharedStateContext from "../../../../SharedStateProvider";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/ext-language_tools';
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import { html as beautifyHtml, css as beautifyCss } from 'js-beautify';

interface WidgetStructure {
  shared_uid: string;
  name: string;
  widget_meta: string;
}

function CreateTemplate() {
  const navigate = useNavigate(); // Initialize the useNavigate hook
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [labelsContent, setLabelsContent] = useState<any[]>([]);
  const [/* errorAccToPageStatus, */ setErrorAccToPageStatus] = useState([]);
  const [, setJsonObject] = useState({});
  const { handleCreateClose } = useContext(SharedStateContext);
  const { designTemplatesData, siteOptions } = useSelector(
    (state: any) => state.template
  );
  const [searchParams] = useSearchParams();

  const [, setDesignTemplatesData] = useState("");
  const [selectedLabels, setSelectedLabels] = useState<any>([]); // State for selected labels
  const [dialogOpen, setDialogOpen] = useState(false); // State for dialog visibility
  const [templateName, setTemplateName] = useState(""); // State for template name
  const [widgetStructure, setWidgetStructure] = useState<WidgetStructure[]>([]); //  State for widget structure
  const [additionalSettings, setAdditionalSettings] = useState(""); // State for additional settings
  const [showErrorMessage, setShowErrorMessage] = useState(false); // State for error message visibility
  const [errorMessage, setErrorMessage] = useState(false);
  const [errorMsg, setErrorMsg] = useState(false);
  const [selectedWidgetStructure, setSelectedWidgetStructure] = useState(""); // State for selected widget structure
  const [html, setHtml] = useState(""); // State for HTML
  const [css, setCss] = useState(""); // State for CSS
  const [htmlFocus, setHtmlFocus] = useState(false);
  const [cssFocus, setCssFocus] = useState(false);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const selectedSite = searchParams.get("selectedSite") as any;
  const location = useLocation();

  useEffect(() => {
    const fetchWebsiteStructure = async () => {
      try {
        if (oauthTokenHeader) {
          await DesignTemplatesEndPointLogic.getWidgetStructures(
            oauthTokenHeader,
            setWidgetStructure
          );
        } else {
          throw new Error("Error fetching website structure data");
        }
      } catch (error) {
        console.error(error, "error");
      }
    };
    fetchWebsiteStructure();
  }, [oauthTokenHeader]);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        if (oauthTokenHeader) {
          await DesignTemplatesEndPointLogic.getLabels(
            oauthTokenHeader,
            setLabelsContent,
            setErrorAccToPageStatus
          );
        } else {
          throw new Error("Error fetching labels data");
        }
      } catch (error) {
        console.error(error, "error");
      }
    };
    fetchLabels();
  }, [oauthTokenHeader, setErrorAccToPageStatus]);

  const labels = labelsContent.map((item: any) => item?.label);

  const createNewTemplate = async () => {
    const data = {
      website_shared_uid: selectedSite,
      component: selectedWidgetStructure,
      name: templateName,
      html: html,
      css: css,
      labels: selectedLabels,
      additional_info: additionalSettings,
    };
    try {
      if (oauthTokenHeader) {
        await DesignTemplatesEndPointLogic.createDesignTemplates(
          oauthTokenHeader,
          data
        );
      } else {
        throw new Error("User doesn't have permissions to create new template");
      }
      setDialogOpen(true);
      setIsLoading(true);
      await DesignTemplatesEndPointLogic.getDesignTemplates(
        oauthTokenHeader,
        selectedSite,
        setDesignTemplatesData
      );
    } catch (error) {
      console.error(error, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const MAX_ADDITIONAL_INFO_LENGTH = 255; // Set this to the maximum length your database allows

    const isValidHTML = (htmlCode: string): boolean => {
  if (!htmlCode) return true; // Empty HTML is considered valid
  
  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(htmlCode, "text/html");
  
  // Check for parser errors
  const hasParserError = parsedDocument.querySelector("parsererror") !== null;
  
  // Check if there's at least one element in the body
  const hasElement = Array.from(parsedDocument.body.childNodes).some(
    (node) => node.nodeType === Node.ELEMENT_NODE
  );
  
  return !hasParserError && hasElement;
};

const isValidCSS = (cssCode: string): boolean => {
  if (!cssCode) return true; // Empty CSS is considered valid
  
  const style = document.createElement("style");
  style.textContent = cssCode;
  document.head.appendChild(style);
  
  const sheet = style.sheet as CSSStyleSheet;
  let isValid = true;
  
  // Check if CSS rules were successfully parsed
  const hasRules = sheet && sheet.cssRules.length > 0;
  
  // For CSS that might not create rules but is still valid, check syntax
  if (!hasRules) {
    const tempElement = document.createElement('div');
    const originalCssText = tempElement.style.cssText;
    tempElement.style.cssText = cssCode;
    
    // If cssText didn't change and original CSS wasn't empty, it's likely invalid
    const cssDidApply = tempElement.style.cssText !== originalCssText || cssCode.trim() === '';
    isValid = cssDidApply;
  }
  
  // Clean up - remove the style element
  document.head.removeChild(style);
  return isValid;
};
  const validate = async () => {
    if (!templateName || !selectedWidgetStructure || !html || !css) {
      setSnackbarOpen(true);
      setSnackbarMessage("Please fill all the fields");
      setIsValid(false);
      return false;
    }

    // Check if templateName is at least 8 characters long and contains only letters and numbers
    const templateNameRegex = /^[a-zA-Z0-9-_&*$#!:|,./\\ ]{8,}$/;
    //  const templateNameRegex = /^[a-zA-Z0-9]{8,}$/;
    if (!templateNameRegex.test(templateName)) {
      setSnackbarOpen(true);
      setSnackbarMessage(
        "Template name must be at least 8 characters long and contain only letters, numbers, and underscores"
      );
      setIsValid(false);
      return false;
    }

    // Check if the template name is unique
    const isTemplateNameUnique = designTemplatesData.every(
      (item: any) => item.name !== templateName
    );

    if (!isTemplateNameUnique) {
      setSnackbarOpen(true);
      setSnackbarMessage("Template name must be unique");
      setIsValid(false);
      return false;
    }

      // Validate Html
    if (!isValidHTML(html)) {
      setSnackbarOpen(true);
      setSnackbarMessage("Invalid HTML Code");
      setErrorMessage(true);
      setIsValid(false);
      return false;
    } else {
      setErrorMessage(false);
    }

    // Validate CSS
    if (!isValidCSS(css)) {
      setSnackbarOpen(true);
      setSnackbarMessage("Invalid CSS Code");
      setErrorMsg(true);
      setIsValid(false);
      return false;
    } else {
      setErrorMsg(false);
    }

    // Check if additionalSettings is a valid JSON
    try {
      if (additionalSettings) {
        const parsedAdditionalInfo = JSON.parse(additionalSettings);
        // Optionally, you can check if it's an object
        if (typeof parsedAdditionalInfo !== "object") {
          throw new Error("Not an object");
        }
      }
    } catch (e) {
      setSnackbarOpen(true);
      setSnackbarMessage("Additional settings must be a valid JSON object");
      setShowErrorMessage(true);
      setIsValid(false);
      return false;
    }

    // Check if additionalSettings exceeds the maximum length
    if (additionalSettings.length > MAX_ADDITIONAL_INFO_LENGTH) {
      setSnackbarOpen(true);
      setSnackbarMessage(
        `Additional settings must not exceed ${MAX_ADDITIONAL_INFO_LENGTH} characters`
      );
      setShowErrorMessage(true);
      setIsValid(false);
      return false;
    }

    setIsValid(true); // Validation passed
    return true; // Validation passed
  };

  const handleCreateTemplate = async () => {
    const isFormValid = await validate();
    if (!isFormValid) return; // Only proceed if the form is valid
    try {
      setLoading(true);
      await createNewTemplate(); // Call the createNewTemplate function
      setIsLoading(true);
      setDialogOpen(true); // Open the dialog
    } catch (error) {
      console.error(error, "error");
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

   const handleFormatHTML = () => {
    if (html?.trim() !== "" && html?.length > 0) {
      const parser = new DOMParser();
      const document = parser.parseFromString(html, "text/html");
      const hasParserError = document.querySelector("parsererror") !== null;
      const hasElement = Array.from(document.body.childNodes).some(
        (node) => node.nodeType === Node.ELEMENT_NODE
      );
      if (!hasParserError && hasElement) {
        const formattedCode = beautifyHtml(html, {
            indent_size: 2,
            indent_char: " ",
            max_preserve_newlines: 2,
            preserve_newlines: true,
            end_with_newline: true,
            wrap_line_length: 0, // No line wrapping
            wrap_attributes: "auto", // "auto", "force", "force-aligned", "force-expand-multiline"
            wrap_attributes_indent_size: 4,
            indent_inner_html: true,
            indent_body_inner_html: true,
            indent_head_inner_html: true,
            unformatted: ["pre", "code", "textarea"], // Tags that shouldn't be formatted
            content_unformatted: ["pre", "textarea"], // Tags whose content shouldn't be formatted
            extra_liners: ["head", "body", "/html"], // Tags that get extra line breaks
          // indent_size: 2,
          // indent_inner_html: true,
          // end_with_newline: true,
        });
        setHtml(formattedCode);
        setErrorMessage(false);
      } else {
        setErrorMessage(true);
      }
    }
  };


  const handleFormatCSS = () => {
    if (css?.trim() !== "" && css?.length > 0) {
      const style = document.createElement("style");
      style.textContent = css;
      document.head.appendChild(style);
      const sheet = style.sheet as CSSStyleSheet;
      const valid = sheet && sheet.cssRules.length > 0;
      if (valid) {
        const formatted = beautifyCss(css, {
          indent_size: 2,
          indent_char: " ",
          selector_separator_newline: true,
          newline_between_rules: true,
          end_with_newline: true,
          preserve_newlines: false,
          max_preserve_newlines: 1,
          wrap_line_length: 0, // No line wrapping
        });
        setCss(formatted);
        setErrorMsg(false);
      } else {
        setErrorMsg(true);
      }
    }
  };

  const handleLabelsChange = (_: any, newValue: string[]) => {
    const newLabels = newValue?.map(value => value?.trim?.() || value)?.filter(value => value);
    const uniqueLabels = [...new Set(newLabels)];
    setSelectedLabels(uniqueLabels);
  };

  const handleCancelClick = () => {
    navigate(`/operations/templates${location.search}`);
  };

  const handleClose = () => {
    setDialogOpen(false);
    handleCreateClose(); // Close the dialog
    setSelectedWidgetStructure("");
    setSelectedLabels([]);
    setTemplateName("");
    setHtml("");
    setCss("");
    setAdditionalSettings("");
    setShowErrorMessage(false);
    setErrorMessage(false);
    setErrorMsg(false);
    navigate(`/operations/templates${location.search}`);
  };

  const widgetStructureOptions = widgetStructure.map((widget) => ({
    label: widget?.name, // Display name in the dropdown
    value: widget?.shared_uid, // Value to be used (shared_uid)
  }));

  const formatJSON = () => {
    try {
      const parsedJSON = JSON.parse(additionalSettings.trim() || "{}");
      const formattedJSON = JSON.stringify(parsedJSON, null, 2);
      setAdditionalSettings(formattedJSON);
      setShowErrorMessage(false);
    } catch (error) {
      setShowErrorMessage(true);
    }
  };

  const renderSnackbar = () => {
    return (
      <Snackbar
        sx={{ zIndex: "9999999" }}
        open={snackbarOpen}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={
            snackbarMessage.includes("Error") || isValid ? "success" : "error"
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    );
  };

  useEffect(() => {
    // get the value of additional_info from the text field
    if (additionalSettings) {
      let value = additionalSettings;
      value = value
        ?.replace(/\\n/g, "")
        ?.replace(/\\\\/g, "")
        ?.replace(/\\"/g, '"'); // Remove "\n", "\\" and extra backslashes
      if (value?.startsWith('"') && value?.endsWith('"')) {
        value = value?.slice(1, -1); // Remove the first and last character
      }
      try {
        const parsedJSON = JSON.parse(value || "{}");
        setJsonObject(parsedJSON);
        setShowErrorMessage(false);
      } catch (error) {
        setShowErrorMessage(true);
      }
    } else {
      setJsonObject({});
      setShowErrorMessage(false);
    }
  }, [additionalSettings])

  return (
    <>
      {renderSnackbar()}
      <Paper
        sx={{
          padding: "8px 16px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography fontSize={24}>Create New Template</Typography>
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            height: "706px",
            paddingRight: "16px",
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Stack>
                  <Typography marginTop={1}>General</Typography>
                  <TextField
                    id="outlined-multiline-flexible"
                    label="Template Name" // Set your label here
                    placeholder="How you like the Theme to be called" // Set your placeholder here
                    multiline
                    required
                    color="secondary"
                    size="small"
                    maxRows={4}
                    value={templateName}
                    onChange={(e) => {
                      setTemplateName(e.target.value);
                    }}
                    sx={{
                      width: "100%",
                      marginTop: "16px",
                    }}
                  />
                  <Typography fontSize={12} marginTop={0.5}>
                    minimum of 8 characters, only letters and numbers.{" "}
                  </Typography>
                  <Autocomplete
                    sx={{ width: "100%", marginTop: "16px" }}
                    options={siteOptions}
                    getOptionLabel={(option) => option.siteName || ""}
                    value={
                      siteOptions?.find((option: any) => {
                        return option.uid === selectedSite;
                      }) || null
                    }
                    disabled
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="What Element does this template refer to"
                        label="Related Website"
                        InputProps={{
                          ...params.InputProps,
                        }}
                        color="secondary"
                        required
                      />
                    )}
                    size="small"
                  />

                  <Autocomplete
                    sx={{ width: "100%", marginTop: "16px" }}
                    options={
                      widgetStructureOptions ? widgetStructureOptions : []
                    }
                    onChange={(_, newValue) => {
                      setSelectedWidgetStructure(
                        newValue ? newValue.value : ""
                      ); // Set the shared_uid of the selected widget
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="What Element does this template refers to"
                        label="Component Type"
                        InputProps={{
                          ...params.InputProps,
                        }}
                        color="secondary"
                        required
                      />
                    )}
                    size="small"
                  />
                  <Typography marginBlock={2}>Labels</Typography>
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    freeSolo
                    id="fixed-tags-demo"
                    value={selectedLabels}
                    onChange={handleLabelsChange}
                    // onChange={(_, newValue: any) => {
                    //   setSelectedLabels(newValue);
                    // }}
                    options={labels}
                    getOptionLabel={(option) => option}
                    renderTags={(tagValue, getTagProps) => (
                      <Box
                        sx={{
                          maxHeight: 180, // Limit height for about 8 chips
                          overflowY: "auto", // Enable scrolling when content overflows
                          display: "flex",
                          flexWrap: "wrap", // Ensure chips wrap to the next line if needed
                          gap: "12px", // Add space between each chip
                        }}
                      >
                        {tagValue.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              label={option}
                              {...tagProps}
                              color="secondary"
                              sx={{
                                backgroundColor: "#7B1FA2",
                                border: "1px solid #7B1FA2",
                                color: "#FFFFFF",
                                "& .MuiChip-deleteIcon": {
                                  color: "#D7BBE3", // Set the color of the delete icon (cross)
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                    sx={{
                      width: "100%",
                      height: "234px",
                      "& .MuiAutocomplete-endAdornment": {
                        top: "9%", // Position the clear button at the top 9%
                        position: "absolute", // Ensure it's positioned relative to the container
                      },
                      "& .MuiAutocomplete-popupIndicator": {
                        display: "none", // This removes the popup indicator (dropdown arrow)
                      },
                    }} // Autocomplete takes full width
                    size="small" // Set Autocomplete size to small
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Label"
                        placeholder="Add new label"
                        color="secondary"
                        sx={{
                          "& .MuiInputBase-root": {
                            minHeight: "234px", // Adjust the height of the textarea here
                            alignItems: "flex-start", // Align the input and placeholder to the top
                          },
                          "& .MuiFormLabel-root": {
                            top: "-5px", // Adjust label position closer to the top
                          },
                          "& .MuiInputBase-input": {
                            paddingTop: "8px", // Add padding to position text at the top
                          },
                        }}
                      />
                    )}
                  />
                  <Box sx={{ marginTop: "68px" }}>
                    <Typography marginBlock={2}>Additional Settings</Typography>
                    <Box sx={{ flex: 1, border: '1px solid #ddd', borderRadius: 1, }}>
                      <AceEditor
                        mode="json"
                        theme="dark" // or "monokai" for dark theme
                        name="json-dictionary-editor"
                        onChange={(value: any) => setAdditionalSettings(value)}
                        value={additionalSettings}
                        fontSize={12}
                        showPrintMargin={false}
                        showGutter={false}
                        highlightActiveLine={true}
                        width="100%"
                        height="80px"
                        setOptions={{
                          enableBasicAutocompletion: false,
                          enableLiveAutocompletion: false,
                          enableSnippets: true,
                          tabSize: 2,
                          useWorker: false, // Important: prevents console errors
                          wrap: true,
                          fontSize: 14,
                          fontFamily: 'Geist Mono, monospace', // Use a monospaced font
                          showLineNumbers: false
                        }}
                        editorProps={{
                          $blockScrolling: true
                        }}
                        style={{
                          borderRadius: '4px',
                        }}
                      />
                      {additionalSettings && showErrorMessage === true ? ( // Conditionally render the warning message
                        <Typography fontSize={12} marginTop={2} color={"#D32F2F"}>
                          Invalid JSON format. Please correct the JSON syntax.
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={8}>
                <Stack flexDirection={"row"} gap={4}>
                  <Grid item xs={6}>
                    <Stack>
                      <Typography marginTop={1} marginBottom={2}>
                        Code
                      </Typography>
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: "-11px",
                            left: "8px",
                            padding: "2px 6px",
                            backgroundColor: "#fff",
                            fontSize: "12px",
                            borderRadius: "4px",
                            zIndex: 1,
                            color: htmlFocus ? "#9C27B0" : "rgba(0, 0, 0, 0.6)",

                            fontFamily:
                              '"Roboto", "Helvetica", "Arial", sans-serif',
                            fontWeight: 400,
                            lineHeight: "1.4375em",
                            letterSpacing: "0.00938em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          HTML
                        </div>
                        <Box
                          sx={{
                            height: "471px",
                            padding: "16.5px 14px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",

                            transition: "border-color 0.2s ease",

                            "&:hover": {
                              borderColor: htmlFocus
                                ? "#9C27B0"
                                : "rgba(0, 0, 0, 0.87)",
                            },

                            ...(htmlFocus && {
                              border: "2px solid #9C27B0",
                            }),
                          }}
                          onFocus={() => setHtmlFocus(true)}
                          onBlur={() => setHtmlFocus(false)}
                        >
                          <AceEditor
                            mode="html"
                            theme="xcode"
                            name="html-dictionary-editor"
                            onChange={(value: any) => setHtml(value)}
                            value={html}
                            onFocus={() => setHtmlFocus(true)}
                            onBlur={() => setHtmlFocus(false)}
                            fontSize={12}
                            showPrintMargin={false}
                            showGutter={false}
                            highlightActiveLine={true}
                            width="100%"
                            height="450px"
                            setOptions={{
                              enableBasicAutocompletion: false,
                              enableLiveAutocompletion: false,
                              enableSnippets: true,
                              tabSize: 2,
                              useWorker: false,
                              wrap: true,
                              fontSize: 14,
                              fontFamily: "Geist Mono, monospace",
                              showLineNumbers: false,
                              copyWithEmptySelection: true,
                            }}
                            editorProps={{
                              $blockScrolling: true,
                            }}
                            style={{
                              borderRadius: "4px",
                            }}
                          />
                          {html && errorMessage === true ? (
                            <Typography
                              fontSize={12}
                              marginTop={2}
                              color={"#D32F2F"}
                            >
                              Invalid HTML format. Please correct the HTML
                              syntax.
                            </Typography>
                          ) : null}
                        </Box>
                        <Button
                          variant="outlined"
                          endIcon={<FormatAlignCenterIcon />}
                          color="secondary"
                          size="small"
                          sx={{ mt: 5, alignSelf: "flex-start" }}
                          disabled={!html}
                          onClick={handleFormatHTML}
                        >
                          Format
                        </Button>
                      </div>
                      {/* <TextField
                        id="outlined-textarea"
                        label="HTML"
                        value={html}
                        onChange={(e) => {
                          setHtml(e.target.value);
                        }}
                        placeholder="{}"
                        multiline
                        rows={19}
                        color="secondary"
                      /> */}
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Stack sx={{ marginTop: "48px" }}>
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: "-11px",
                            left: "8px",
                            padding: "2px 6px",
                            backgroundColor: "#fff",
                            fontSize: "12px",
                            borderRadius: "4px",
                            zIndex: 1,
                            color: cssFocus ? "#9C27B0" : "rgba(0, 0, 0, 0.6)",

                            fontFamily:
                              '"Roboto", "Helvetica", "Arial", sans-serif',
                            fontWeight: 400,
                            lineHeight: "1.4375em",
                            letterSpacing: "0.00938em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          CSS
                        </div>
                        <Box
                          sx={{
                            height: "471px",
                            padding: "16.5px 14px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",

                            transition: "border-color 0.2s ease",

                            "&:hover": {
                              borderColor: cssFocus
                                ? "#9C27B0"
                                : "rgba(0, 0, 0, 0.87)",
                            },

                            ...(cssFocus && {
                              border: "2px solid #9C27B0",
                            }),
                          }}
                          onFocus={() => setCssFocus(true)}
                          onBlur={() => setCssFocus(false)}
                        >
                          <AceEditor
                            mode="css"
                            theme="xcode"
                            name="css-dictionary-editor"
                            onChange={(value: any) => setCss(value)}
                            value={css}
                            onFocus={() => setCssFocus(true)}
                            onBlur={() => setCssFocus(false)}
                            fontSize={12}
                            showPrintMargin={false}
                            showGutter={false}
                            highlightActiveLine={true}
                            width="100%"
                            height="450px"
                            setOptions={{
                              enableBasicAutocompletion: false,
                              enableLiveAutocompletion: false,
                              enableSnippets: true,
                              tabSize: 2,
                              useWorker: false,
                              wrap: true,
                              fontSize: 14,
                              fontFamily: "Geist Mono, monospace",
                              showLineNumbers: false,
                              copyWithEmptySelection: true,
                            }}
                            editorProps={{
                              $blockScrolling: true,
                            }}
                            style={{
                              borderRadius: "4px",
                            }}
                          />
                          {css && errorMsg === true ? (
                            <Typography
                              fontSize={12}
                              marginTop={2}
                              color={"#D32F2F"}
                            >
                              Invalid CSS format. Please correct the CSS syntax.
                            </Typography>
                          ) : null}
                        </Box>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          endIcon={<FormatAlignCenterIcon />}
                          sx={{ mt: 5, alignSelf: "flex-start" }}
                          disabled={!css}
                          onClick={handleFormatCSS}
                        >
                          Format
                        </Button>
                      </div>
                      {/* <TextField
                        id="outlined-textarea"
                        label="CSS"
                        value={css}
                        onChange={(e) => {
                          setCss(e.target.value);
                        }}
                        placeholder="{}"
                        multiline
                        rows={19}
                        color="secondary"
                        sx={{ marginTop: "48px" }}
                      /> */}
                    </Stack>
                  </Grid>
                </Stack>
                <SnackbarContent
                  sx={{
                    background: "#FDEDED",
                    color: "#5F2120",
                    marginTop: "48px",
                    boxShadow: "none",
                    border: "1px solid #D32F2F80",
                    padding: "0 4px",
                  }}
                  message={
                    <Box display="flex">
                      <ErrorOutlineIcon
                        sx={{ marginRight: "8px", color: "#5F2120" }}
                      />
                      <Box>
                        <Typography
                          variant="body1"
                          fontWeight="500"
                          sx={{ marginBottom: "4px" }}
                        >
                          Update with Caution:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2, // Limit the text to 2 lines
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis", // Adds ellipsis if text is too long
                          }}
                        >
                          Please proceed with extreme care when making updates,
                          as these changes may adversely affect the overall
                          design functionality. Ensure thorough testing and
                          validation to avoid any unintended disruptions or
                          errors in the end user experience.
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box>
          <Stack
            flexDirection={"row"}
            justifyContent={"space-between"}
            marginTop={2}
            gap={1}
          >
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              endIcon={<FormatAlignCenterIcon />}
              disabled={!additionalSettings}
              onClick={formatJSON}
            >
              Format
            </Button>
            <Stack flexDirection={"row"} gap={1}>
              <Button color="secondary" variant="outlined">
                <Stack
                  flexDirection={"row"}
                  gap={1}
                  onClick={handleCancelClick}
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                  Cancel
                </Stack>
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={
                  !templateName || !selectedWidgetStructure || !html || !css || errorMessage || errorMsg || showErrorMessage
                }
                onClick={handleCreateTemplate}
                data-tracker="149"
                sx={{ textTransform: "uppercase" }}
              >
                <Stack flexDirection={"row"} gap={1}>
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Create template"
                  )}
                  <CheckIcon fontSize="small" />
                </Stack>
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
      {/* Dialog component */}
      <Dialog
        open={dialogOpen}
        PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <DialogContent>
            <Typography variant="h6">Template Created Successfully!</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              color="secondary"
              variant="contained"
              onClick={handleClose}
              sx={{ textTransform: "uppercase" }}
            >
              Close
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>
    </>
  );
}

export default CreateTemplate;
